/*
 * F-05 · LA MARCA DE MESES EN EL RENGLÓN DE LA TARJETA (RF-183, RF-184,
 * RN-554 a RN-557).
 *
 * Reparte `totalesPorTarjeta` —que llega de UNA SOLA llamada a
 * ConsultarMensualidadesDePeriodo, sin `idCuenta`— entre los renglones ya pintados.
 *
 * LA CIFRA ES UN REPARTO DE DEUDA YA CONTADA, NO DEUDA ADICIONAL (D-53, RN-103c,
 * RN-555): queda PROHIBIDO sumarla a la deuda del renglón, al total de deuda por
 * moneda o restarla del disponible. Por eso este módulo NO suma nada: solo elige
 * qué texto mostrar. Si al leer el renglón alguien puede entender que la tarjeta
 * debe la deuda MÁS los meses, el renglón está mal.
 */

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';

/** Los tres casos del renglón, y no hay un cuarto (RN-556, RN-557). */
export const MARCAS_DE_MESES = Object.freeze({
  // Sin planes vigentes, o el 4/6 no respondió: NO se muestra marca ni cifra. No se
  // pinta un cero ni un «sin meses», que sería ruido en el caso normal.
  SIN_MARCA: 'SIN_MARCA',
  // Con planes y con importe que cae en este corte.
  CON_IMPORTE: 'CON_IMPORTE',
  // Con planes y NADA en este corte. Se dice con palabras, nunca con $0.00: la
  // tarjeta sí tiene planes, y ocultar la marca mentiría tanto como pintar un cero
  // donde se espera una cifra que cae (RN-557.1).
  NADA_EN_ESTE_CORTE: 'NADA_EN_ESTE_CORTE',
});

/**
 * Indexa los totales por `idCuenta` para repartirlos sin volver a consultar.
 * Si el 4/6 no respondió se recibe `null` y el mapa queda vacío: ninguna tarjeta
 * muestra marca y la lista se comporta exactamente como antes (RN-556, RNF-103).
 */
export function indexarTotalesPorTarjeta(totalesPorTarjeta) {
  const mapa = new Map();
  (totalesPorTarjeta ?? []).forEach((total) => {
    if (total?.idCuenta !== undefined && total?.idCuenta !== null) mapa.set(total.idCuenta, total);
  });
  return mapa;
}

/**
 * Qué muestra el renglón de ESTA tarjeta.
 *
 * @param tarjeta el `CuentaDto` del renglón.
 * @param total   su entrada de `totalesPorTarjeta`, o `undefined` si no hay.
 * @returns {{marca:string, texto:string|null, etiquetaDeLectura:string|null}}
 */
export function resolverMarcaDeMeses(tarjeta, total) {
  const sinMarca = { marca: MARCAS_DE_MESES.SIN_MARCA, texto: null, cifra: null, etiquetaDeLectura: null };

  if (!tarjeta?.esCredito || !total) return sinMarca;
  if (!(Number(total.cantidadPlanesVigentes ?? 0) > 0)) return sinMarca;

  const imputado = Number(total.totalImputadoAlMes ?? NaN);
  if (!Number.isFinite(imputado)) return sinMarca;

  if (imputado === 0) {
    return {
      marca: MARCAS_DE_MESES.NADA_EN_ESTE_CORTE,
      texto: 'a meses: nada en este corte',
      cifra: null,
      etiquetaDeLectura: 'con compras a meses y nada en este corte',
    };
  }

  // El importe se formatea con la divisa de la TARJETA y nunca se combina con otra
  // moneda (RN-557.2).
  const cifra = formatearImporte(total.totalImputadoAlMes, tarjeta.codigoISO);
  return {
    marca: MARCAS_DE_MESES.CON_IMPORTE,
    texto: `A meses este corte ${cifra}`,
    cifra,
    etiquetaDeLectura: `a meses este corte ${cifra}`,
  };
}
